import React from 'react';
import PropTypes, { any } from 'prop-types';
import moment from 'moment';

import Avatar from './CardViewComponent/Avatar';
import { Description } from '../../text';
import Rating from './SharedComponents/Rating';
import { gameTypes } from '../../../utils/constants';
import { PlayersHeaderColumns } from '../../../pages/SearchPage/SortableColumnHeaders';
import { sportTypeIds } from '../../../utils/eventConstants';
import PopOverMenu from '../../PopOverMenu';
import RowPlayerBookmark from '../../SavedLists/PlayerBookmark/RowPlayerBookmark';
import { BOOKMARK_ROW_ICON_HEIGHT } from '../../SavedLists/PlayerBookmark';
import { getUserAgeAtEventStart } from '../../../utils/events';

// Global-search mobile cards wrap long names and keep the actions/flag pinned; every other mobile
// PlayerCard surface keeps the original single-line/ellipsis layout. Derived here, out of the component
// body, so the surface-specific class set does not inflate CardView's cognitive complexity.
const getSearchLayoutClasses = (searchLayout) => ({
    nameBlockClassName: searchLayout ? 'name-and-place d-flex align-items-center' : 'name-and-place show-ellipsis d-flex',
    nameBlockStyle: searchLayout ? { flex: '1 1 0', minWidth: 0 } : undefined,
    textEllipsisClassName: searchLayout ? '' : ' show-ellipsis',
    actionsClassName: `ml-auto${searchLayout ? ' flex-shrink-0' : ''}`,
    bookmarkClassName: `d-inline-block text-v3-blue${searchLayout ? ' ml-2' : ''}`,
});

const CardView = props => {
    const {
        user,
        showAlways,
        hideRatings,
        player,
        rank,
        enableMessaging,
        utrType,
        paymentStatus,
        showPlayerPaymentStatus,
        regStatus,
        regStatusColor,
        showRegStatus,
        showSeeProfileLink,
        showRatingNotLoggedIn,
        showDecimals,
        menuOptions,
        optionSpaceContent,
        onMessageClick,
        ptsRank,
        showProjectedViewStatus,
        showPickleBallRatings = false,
        showTennisRatings = true,
        hideUtrLabel,
        sportTypeId,
        filterType,
        players,
        sortResults,
        userLoggedIn,
        sportTypes,
        isItGlobalSearch,
        onMenuSelected,
        showOrganizerViewForPTTEvent,
        eventStartUtc,
        savedBookmark,
        'data-testid': dataTestId,
    } = props;

    let tennis;
    let pickleball;

    if (sportTypes) {
        ({ tennis, pickleball } = sportTypes);
    }
    let gridTemplateColumns = '';
    if (tennis && pickleball) {
        gridTemplateColumns = '1fr 1fr 1fr 1fr';
    } else {
        gridTemplateColumns = '1fr 1fr';
    }
    const {
        ratingStatusSingles,
        ratingStatusDoubles,
        descriptionItemsMobile,
        clubCollegeDetailsMobile,
        playerProfileImages,
        nationality,
        firstName,
        lastName,
    } = player;

    const showPickleBallInfo = showPickleBallRatings && pickleball && (user || showRatingNotLoggedIn);
    const age = getUserAgeAtEventStart(player.birthDate, eventStartUtc);
    const utcNow = moment.utc();
    // Global-search mobile cards wrap long names and keep the actions/flag pinned; every other mobile
    // PlayerCard surface keeps the original single-line/ellipsis layout, so gate the layout tweaks here.
    // (The bookmark's blue colour + design size are the ticket's cross-surface change and stay global.)
    const searchLayout = isItGlobalSearch;
    // No flex-nowrap: the name block is flex:1 1 0 (basis 0) so it grows into the free space and keeps the
    // bookmark on the same line, while the row can still wrap as a last resort instead of overflowing the card.
    const rowClassName = 'row';
    const { nameBlockClassName, nameBlockStyle, textEllipsisClassName, actionsClassName, bookmarkClassName } = getSearchLayoutClasses(searchLayout);
    return (
        <div className={`card-view ${showAlways ? 'd-block' : 'd-block d-md-none'}`}>
            <div className="badge-container">
                {showRegStatus && <div className={`badge registration-status text-small ${regStatusColor}`} data-testid={dataTestId && `${dataTestId}.reg-status-badge`}>{regStatus}</div>}
                {showPlayerPaymentStatus && <div className={`badge payment-status text-small`} data-testid={dataTestId && `${dataTestId}.payment-status-badge`}>{paymentStatus}</div>}
            </div>
            <div className={rowClassName}>
                <div className={nameBlockClassName} style={nameBlockStyle}>
                    <Avatar playerImages={playerProfileImages} nationality={nationality} />
                    <div className="ml-4" style={nameBlockStyle}>
                        <div className={`name h6${textEllipsisClassName}`} data-testid={dataTestId && `${dataTestId}.name`}>{!!rank && <span className="rank" data-testid={dataTestId && `${dataTestId}.rank`}>{rank}.&nbsp;</span>}{firstName} {lastName}</div>
                        <div className={`place${textEllipsisClassName}`}>
                            <Description desc={clubCollegeDetailsMobile || descriptionItemsMobile} data-testid={dataTestId && `${dataTestId}.place`} />
                        </div>
                        {showOrganizerViewForPTTEvent && player?.college && player?.gradYear && moment(player?.gradYear)?.isAfter(utcNow) && (
                            <div className={`place${textEllipsisClassName}`} data-testid={dataTestId && `${dataTestId}.college-details`}>{`${player.college} (\u2018${moment(player.gradYear).format('YY')})`}</div>
                        )}
                    </div>
                </div>
                <div className={actionsClassName}>
                    {enableMessaging && (
                        <div
                            className="click-area d-inline-block d-md-none"
                            onClick={(ev) => {
                                ev.stopPropagation();
                                ev.preventDefault();
                                onMessageClick();
                            }}
                            data-testid={dataTestId && `${dataTestId}.message-btn`}
                        >
                            <i className={`utr utr-message fs18 text-teal`} />
                        </div>
                    )}
                    {menuOptions?.length > 0 && (
                        <PopOverMenu
                            horizontalEllipsis={false}
                            menuItems={menuOptions}
                            onSelected={onMenuSelected}
                            popOverMenuClass={`d-inline-block`}
                            data-testid={dataTestId && `${dataTestId}.popover-menu`}
                        />
                    )}
                    <RowPlayerBookmark
                        savedBookmark={savedBookmark}
                        className={bookmarkClassName}
                        size={BOOKMARK_ROW_ICON_HEIGHT}
                        data-testid={dataTestId && `${dataTestId}.player-bookmark`}
                    />
                </div>
            </div>
            <div className="divider" />
            <PlayersHeaderColumns
                filterType={filterType}
                players={players}
                sortResults={sortResults}
                utrType={utrType}
                userLoggedIn={userLoggedIn}
                sportTypes={sportTypes}
                showFilterDropDown="hide"
                showHeaderColumnsInMobile={true}
                isMobileView={true}
            />
            <div className="d-grid profile-details " style={{ gridTemplateColumns, marginTop: 0, paddingTop: 0 }}>
                {!hideRatings && (
                    <>
                        {(isItGlobalSearch ? (showTennisRatings && tennis) : showTennisRatings) && (
                            <div className="flex-1">
                                {(user || showRatingNotLoggedIn) ? (
                                    <Rating
                                        utrType={utrType}
                                        ratingType={gameTypes.SINGLES}
                                        ratingStatus={ratingStatusSingles}
                                        player={player}
                                        showDecimals={showDecimals}
                                        showProjectedViewStatus={showProjectedViewStatus}
                                        hideUtrLabel={hideUtrLabel}
                                        sportTypeId={sportTypeId}
                                        isItGlobalSearch={isItGlobalSearch}
                                        data-testid={dataTestId && `${dataTestId}.tennis-singles-rating`}
                                    />
                                ) : (
                                    <div className="values">&nbsp;</div>
                                )}
                            </div>
                        )}
                        {(isItGlobalSearch ? (showTennisRatings && tennis) : showTennisRatings) && (
                            <div className="flex-1">
                                {(user || showRatingNotLoggedIn) ? (
                                    <Rating
                                        utrType={utrType}
                                        ratingType={gameTypes.DOUBLES}
                                        ratingStatus={ratingStatusDoubles}
                                        player={player}
                                        showDecimals={showDecimals}
                                        showProjectedViewStatus={showProjectedViewStatus}
                                        hideUtrLabel={hideUtrLabel}
                                        sportTypeId={sportTypeId}
                                        isItGlobalSearch={isItGlobalSearch}
                                        data-testid={dataTestId && `${dataTestId}.tennis-doubles-rating`}
                                    />
                                ) : (
                                    <div className="values">&nbsp;</div>
                                )}
                            </div>
                        )}
                        {showPickleBallInfo && (
                            <div>
                                <Rating
                                    utrType={utrType}
                                    ratingType={gameTypes.SINGLES}
                                    ratingStatus={ratingStatusSingles}
                                    player={player}
                                    showDecimals={showDecimals}
                                    showProjectedViewStatus={showProjectedViewStatus}
                                    hideUtrLabel={hideUtrLabel}
                                    sportTypeId={sportTypeId}
                                    isPickleballRating={true}
                                    isItGlobalSearch={isItGlobalSearch}
                                    data-testid={dataTestId && `${dataTestId}.pkb-singles-rating`}
                                />
                            </div>
                        )}
                        {showPickleBallInfo && (
                            <div>
                                <Rating
                                    utrType={utrType}
                                    ratingType={gameTypes.DOUBLES}
                                    ratingStatus={ratingStatusDoubles}
                                    player={player}
                                    showDecimals={showDecimals}
                                    showProjectedViewStatus={showProjectedViewStatus}
                                    hideUtrLabel={hideUtrLabel}
                                    sportTypeId={sportTypeId}
                                    isPickleballRating={true}
                                    isItGlobalSearch={isItGlobalSearch}
                                    data-testid={dataTestId && `${dataTestId}.pkb-doubles-rating`}
                                />
                            </div>
                        )}
                        {ptsRank && (
                            <div className="flex-1">
                                <div className="values">
                                    <div className="title" data-testid={dataTestId && `${dataTestId}.ptt-rank.label`}>
                                        PTT
                                    </div>
                                    <div
                                        className={`value`}
                                        title={`PTT Rank`}
                                        data-testid={dataTestId && `${dataTestId}.ptt-rank.value`}
                                    >
                                        {ptsRank}
                                    </div>
                                </div>
                            </div>
                        )}
                        {showOrganizerViewForPTTEvent && (
                            <div className="flex-1">
                                <div className="values">
                                    <div className="title" data-testid={dataTestId && `${dataTestId}.age.label`}>
                                        Age
                                    </div>
                                    <div
                                        className={`value`}
                                        title={`Players age at start of event`}
                                        data-testid={dataTestId && `${dataTestId}.age.value`}
                                    >
                                        {age}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div className={`justify-content-end`}>
                    {enableMessaging && (
                        <div
                            className="click-area d-none d-md-block"
                            onClick={(ev) => {
                                ev.stopPropagation();
                                ev.preventDefault();
                                onMessageClick();
                            }}
                            data-testid={dataTestId && `${dataTestId}.message-btn-desktop`}
                        >
                            <i className={`utr utr-message fs18 text-teal`} />
                        </div>
                    )}
                    {!menuOptions && showSeeProfileLink && (
                        <span className={`extra-card-link`} data-testid={dataTestId && `${dataTestId}.see-profile-link`}>See Profile</span>
                    )}
                    {!!optionSpaceContent && !menuOptions && (
                        <div className="fs14" data-testid={dataTestId && `${dataTestId}.option-space`}>{optionSpaceContent}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

CardView.propTypes = {
    user: PropTypes.objectOf(any).isRequired,
    showAlways: PropTypes.bool,
    hideRatings: PropTypes.bool,
    player: PropTypes.objectOf(any).isRequired,
    rank: PropTypes.number,
    enableMessaging: PropTypes.bool,
    utrType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    paymentStatus: PropTypes.string,
    showPlayerPaymentStatus: PropTypes.bool,
    regStatus: PropTypes.string,
    regStatusColor: PropTypes.string,
    showRegStatus: PropTypes.bool,
    showSeeProfileLink: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showDecimals: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(any),
    optionSpaceContent: PropTypes.string,
    onMessageClick: PropTypes.func,
    ptsRank: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    showProjectedViewStatus: PropTypes.bool,
    showPickleBallRatings: PropTypes.bool,
    showTennisRatings: PropTypes.bool,
    hideUtrLabel: PropTypes.bool,
    sportTypeId: PropTypes.number,
    isItGlobalSearch: PropTypes.bool,
    onMenuSelected: PropTypes.func,
    showOrganizerViewForPTTEvent: PropTypes.bool,
    eventStartUtc: PropTypes.string,
    savedBookmark: PropTypes.instanceOf(Object),
    'data-testid': PropTypes.string,
};

CardView.defaultProps = {
    showPickleBallRatings: false,
    showTennisRatings: true,
    hideUtrLabel: false,
    showAlways: false,
    hideRatings: false,
    rank: null,
    enableMessaging: false,
    utrType: null,
    paymentStatus: '',
    showPlayerPaymentStatus: false,
    regStatus: '',
    regStatusColor: '',
    showRegStatus: false,
    showSeeProfileLink: false,
    showRatingNotLoggedIn: false,
    showDecimals: false,
    menuOptions: [],
    optionSpaceContent: '',
    onMessageClick: () => { },
    onMenuSelected: () => { },
    ptsRank: null,
    showProjectedViewStatus: false,
    sportTypeId: sportTypeIds.TENNIS,
    isItGlobalSearch: false,
    showOrganizerViewForPTTEvent: false,
    eventStartUtc: null,
    savedBookmark: null,
    'data-testid': null,
};

export default CardView;
